import { useEffect, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const STYLE_TAGS = ["Value", "Growth", "Momentum", "Dividend", "Contrarian", "Passive", "Swing", "Long-term", "Index"];
const SECTORS = ["IT", "Banking", "FMCG", "Pharma", "Auto", "Energy", "Metals", "Real Estate", "Consumer Durables", "Telecom", "PSU"];

function TagRow({ label, options, selected, onToggle, testPrefix }) {
  return (
    <div>
      <Label className="text-zinc-300">{label}</Label>
      <div className="flex flex-wrap gap-2 mt-2">
        {options.map((t) => {
          const active = selected.includes(t);
          return (
            <button key={t} type="button" onClick={() => onToggle(t)} data-testid={`${testPrefix}-${t}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                active
                  ? "bg-emerald-600/20 border-emerald-700/60 text-emerald-300"
                  : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
              }`}>
              {t}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Style() {
  const [style, setStyle] = useState(null);

  useEffect(() => { (async () => { const { data } = await http.get("/style"); setStyle(data); })(); }, []);

  if (!style) return <div className="text-zinc-500 text-sm">Loading…</div>;

  const toggle = (key, val) => {
    setStyle((s) => {
      const list = s[key] || [];
      return { ...s, [key]: list.includes(val) ? list.filter((x) => x !== val) : [...list, val] };
    });
  };

  const save = async () => {
    try {
      await http.put("/style", style);
      toast.success("Investing style saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const riskLabel = ["Ultra-safe","Very cautious","Cautious","Balanced-low","Balanced","Balanced-high","Growth","Aggressive","Very aggressive","YOLO"][style.risk_tolerance - 1] || "";

  return (
    <div className="max-w-3xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-bold">Style Trainer</div>
        <h1 className="mt-2 font-display text-4xl font-light">Train your investing DNA</h1>
        <p className="text-zinc-400 mt-2 text-sm">The AI advisor uses this profile to tailor buy / sell / hold suggestions.</p>
      </div>

      <div className="space-y-8 bg-zinc-900 border border-zinc-800 rounded-lg p-8">
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-zinc-300">Risk tolerance</Label>
            <div className="text-xs">
              <span className="text-zinc-500">Level</span>{" "}
              <Badge className="bg-emerald-600/20 text-emerald-300 border-emerald-800" data-testid="risk-badge">
                {style.risk_tolerance}/10 · {riskLabel}
              </Badge>
            </div>
          </div>
          <div className="mt-4">
            <Slider value={[style.risk_tolerance]} min={1} max={10} step={1}
              onValueChange={([v]) => setStyle({ ...style, risk_tolerance: v })}
              data-testid="risk-slider" />
          </div>
        </div>

        <div>
          <Label className="text-zinc-300">Investment horizon (years)</Label>
          <Input type="number" min={1} max={40} value={style.horizon_years} data-testid="horizon-input"
            onChange={(e) => setStyle({ ...style, horizon_years: parseInt(e.target.value || "1") })}
            className="mt-2 bg-zinc-950 border-zinc-800 w-32" />
        </div>

        <div>
          <Label className="text-zinc-300">Max weight of a single position (%)</Label>
          <Input type="number" min={5} max={100} step={0.5} value={style.max_single_position_pct} data-testid="max-pos-input"
            onChange={(e) => setStyle({ ...style, max_single_position_pct: parseFloat(e.target.value || "20") })}
            className="mt-2 bg-zinc-950 border-zinc-800 w-32" />
        </div>

        <TagRow label="Style tags" options={STYLE_TAGS} selected={style.style_tags || []}
          onToggle={(v) => toggle("style_tags", v)} testPrefix="style-tag" />

        <TagRow label="Preferred sectors" options={SECTORS} selected={style.sectors_preferred || []}
          onToggle={(v) => toggle("sectors_preferred", v)} testPrefix="sector-pref" />

        <TagRow label="Sectors to avoid" options={SECTORS} selected={style.sectors_avoided || []}
          onToggle={(v) => toggle("sectors_avoided", v)} testPrefix="sector-avoid" />

        <div>
          <Label className="text-zinc-300">Notes for the AI (optional)</Label>
          <Input value={style.notes || ""} onChange={(e) => setStyle({ ...style, notes: e.target.value })}
            className="mt-2 bg-zinc-950 border-zinc-800" placeholder="I prefer to trim winners at +40% and never average down."
            data-testid="style-notes-input" />
        </div>

        <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-6" data-testid="style-save-button">
          Save style profile
        </Button>
      </div>
    </div>
  );
}
